import { type FC, useCallback, useEffect, useMemo } from 'react'

import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import { $tiers, removeTierFx } from '@/models/lists/tiers.ts'

interface Props extends DialogProps {
  tierId: number
}

export const RemoveTierDialog: FC<Props> = ({ isOpen, onClose, tierId }) => {
  const isRemoving = useUnit(removeTierFx.pending)
  const tiers = useUnit($tiers)
  const tier = useMemo(
    () => tiers.find(({ id }) => id === tierId),
    [tierId, tiers],
  )!

  const handleSubmit = useCallback(async () => {
    await removeTierFx(tierId)
    onClose()
  }, [onClose, tierId])

  useEffect(() => {
    if (!tier) {
      onClose()
    }
  }, [onClose, tier])

  if (!tier) {
    return null
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      testId="remove-fan-spend-list-dialog"
    >
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description className="text-center">
          This will permanently remove the {tier.name} list
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.ClosableFooter
        className="mt-6"
        onClose={onClose}
      >
        <Button
          className="flex items-center gap-2"
          variant="destructive"
          onClick={handleSubmit}
          isLoading={isRemoving}
          testId="submit-button"
        >
          Remove
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
